import { Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom'
import { Suspense } from 'react'
import defaultRoutes from './routes'
import { useEffect, useCallback, useRef, useState } from 'react'
import { Alert, InfoBanner, Loader, Modal, RouteErrorBoundary, ScrollToTopOnNavigate } from 'components'
import NotificationPrompt from '../components/notificationPrompt'
import * as api from 'api'
import { getPlanStatus, getTimeFormats } from 'helpers'
import { getAuthUserData, setAuthUserData, getAuthToken, clearAuthData } from '../utils/authManager'
import { logActivity } from 'utils/activityTracker'
import { usePushNotifications } from '../hooks/usePushNotifications'

// Listen for push notifications from service worker
const PushNotificationListener = () => {
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data && event.data.type === 'PUSH_NOTIFICATION') {
          // Show in-page alert with orange theme
          Alert.info(`🔔 ${event.data.title}: ${event.data.body}`)
          
          // Also try to show browser notification if permission is granted
          if (Notification.permission === 'granted') {
            new Notification(event.data.title, {
              body: event.data.body,
              icon: '/logo.png',
              tag: 'push-' + Date.now()
            })
          }
        }
      })
    }
  }, [])
  
  return null
}

// Loading fallback component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    height: '100vh',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    <Loader color='var(--c-primary)' colorText='var(--c-primary)' />
  </div>
)

export default function Main() {
  const routes = [...defaultRoutes]
  const userData = getAuthUserData() // Use secure user data getter
  const processing = useRef()

  const isToday = useCallback((someDate) => {
    const today = new Date()
    return (
      someDate.getDate() === today.getDate() &&
      someDate.getMonth() === today.getMonth() &&
      someDate.getFullYear() === today.getFullYear()
    )
  }, [])

  let planStatus = ''
  if (userData) {
    const planStatusShownDate = localStorage.getItem('planStatusShownDate')
    if (!planStatusShownDate || !isToday(new Date(planStatusShownDate)))
      planStatus = getPlanStatus(userData.planExpiresAt)
  }

  const InfoBannerHandler = useCallback(() => {
    localStorage.setItem('planStatusShownDate', new Date().toISOString())
  }, [])

  const getUserData = useCallback(async () => {
    if (processing.current) return

    processing.current = true

    const userRefData = await api.auth.user.getUser({})

    if (userRefData.code === 201) setAuthUserData(userRefData.payload.userData) // Use secure user data setter

    processing.current = false
  }, [])

  useEffect(() => {
    if (getAuthToken()) getUserData() // Use secure token getter
  }, [getUserData])

  return (
    <Router>
      <Alert.Component />
      <Modal.Component />
      <NotificationPrompt />
      <PushNotificationListener />
      <ScrollToTopOnNavigate />
      <RouteActivityTracker />
      {!!planStatus && planStatus !== 'Active' && userData.plan !== 'Plan 0' && (
        <InfoBanner
          message={'Your plan expiring on ' + getTimeFormats(new Date(userData.planExpiresAt))}
          handler={InfoBannerHandler}
        />
      )}
      <Routes>
        <Route path='/' element={<Redirect />} />

        {routes.map(({ path, Component, Super, auth }, i) => (
          <Route
            key={i}
            path={path}
            element={
              <RouteErrorBoundary>
                <Suspense fallback={<LoadingFallback />}>
                  {auth ? (
                    <Auth>
                      <Super>
                        <Component />
                      </Super>
                    </Auth>
                  ) : Super ? (
                    <Super>
                      <Component />
                    </Super>
                  ) : (
                    <Component />
                  )}
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        ))}

        <Route path='*' element={<Redirect />} />
      </Routes>
    </Router>
  )
}

const RouteActivityTracker = () => {
  const location = useLocation()

  useEffect(() => {
    logActivity({
      eventType: 'page_view',
      targetType: 'page',
      metadata: {
        path: location.pathname,
        search: location.search
      }
    })
  }, [location])

  return null
}

const Redirect = () => {
  const navigate = useNavigate()

  useEffect(() => {
    navigate('/home', { replace: true })
  }, [navigate])
  return null
}

const Auth = ({ children }) => {
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const authenticated = useRef(false)
  const processing = useRef()
  const location = useLocation()
  
  // Initialize push notifications for authenticated users
  usePushNotifications()

  const authTest = useCallback(async () => {
    if (processing.current) return

    processing.current = true

    if (!authenticated.current) {
      const signInAPI = await api.auth.user.getUser({})

      if (signInAPI.code === 201) {
        authenticated.current = true
        setAuthUserData(signInAPI.payload.userData) // Use secure user data setter
        setLoading(false)
      } else if (signInAPI.code === 401) {
        authenticated.current = true
        setLoading(false)
        const userData = getAuthUserData() // Use secure user data getter
        if (userData) {
          userData.plan = 'Plan 0'
          setAuthUserData(userData) // Use secure user data setter
        }
        if (window.location.pathname !== '/userAdmin/subscriptionInfo') {
          Modal.Confirm('You need to buy a plan to use this feature', () => navigate('/renewPlan'))
          navigate('/home', { replace: true })
        }
      } else {
        clearAuthData() // Use secure auth clearing
        navigate('/login', { state: location, replace: true })
      }
    } else setLoading(false)
    processing.current = false
  }, [navigate, location])

  useEffect(() => {
    if (!getAuthToken()) navigate('/login', { state: location, replace: true }) // Use secure token getter
    else authTest()
  }, [authTest, navigate, location])

  return loading ? <Loader /> : <div style={{ overflow: 'hidden' }}>{children}</div>
}
